/**
 * Injects Google Fonts (Fraunces + DM Sans) into the document head once.
 *
 * Shadow DOM components inherit @font-face declarations from the document,
 * so injecting once at the document level makes the fonts available to
 * every <bi-explorer-ai-chat> instance without repeated network requests.
 *
 * Hosts that want to ship their own fonts can opt-out by setting
 * `window.__biaSkipFontInjection = true` before the widget loads.
 */

const MARKER = 'data-bia-fonts';
const HREF =
  'https://fonts.googleapis.com/css2?' +
  'family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;1,9..144,500;1,9..144,600' +
  '&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700' +
  '&display=swap';

export function ensureFontsLoaded(): void {
  try {
    if (typeof document === 'undefined') return;
    if ((window as unknown as { __biaSkipFontInjection?: boolean }).__biaSkipFontInjection) return;
    // Skip in test environments (happy-dom / jsdom) — they reject external
    // stylesheets with non-blocking errors that pollute test output.
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (ua.includes('HappyDOM') || ua.includes('jsdom')) return;
    if (document.querySelector(`link[${MARKER}]`)) return;

    const head = document.head;
    if (!head) return;

    // Preconnect for snappier first paint
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    preconnect1.setAttribute(MARKER, 'preconnect');
    head.appendChild(preconnect1);

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    preconnect2.setAttribute(MARKER, 'preconnect-gstatic');
    head.appendChild(preconnect2);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = HREF;
    link.setAttribute(MARKER, 'true');
    head.appendChild(link);
  } catch {
    // happy-dom or other restricted environments may throw — fonts are
    // a progressive enhancement, never block widget bootstrap on this.
  }
}
